// Web Push wiring for the client.
//
// - The viewer subscribes (enablePush) and registers its subscription with the
//   Worker, keyed by the host it's watching.
// - The host calls notifyCry() when it detects crying; the Worker then fans the
//   push out to every viewer subscribed to that host.
//
// The VAPID public key is not a secret — it's the applicationServerKey the push
// service uses to verify the Worker's signed requests. The matching private key
// lives only in the Worker (turn-worker/.dev.vars locally, a secret in prod).

const VAPID_PUBLIC_KEY =
  'BJb6yFmAvl5d1i6hO1nDRaePQfdYKwTCV4qDfbOvJeapu2jUxlTGtA2-GHsQ1_JYc3aXus4TXmUqfstEJiD2Bu0';

// Where the Worker lives. Reuses the TURN worker origin (it hosts these routes
// too): explicit override > <meta turn-credentials-url> (minus its path) >
// local dev default > same-origin.
export function workerBase() {
  if (window.PEEKABABY_WORKER_URL) return stripPath(window.PEEKABABY_WORKER_URL);
  const meta = document.querySelector('meta[name="turn-credentials-url"]');
  const fromMeta = meta && meta.content && meta.content.trim();
  if (fromMeta) return fromMeta.replace(/\/turn-credentials\/?$/, '');
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') return 'http://localhost:8787';
  return '';
}

function stripPath(url) {
  try {
    const u = new URL(url);
    return u.origin;
  } catch (e) {
    return url.replace(/\/+$/, '');
  }
}

function urlBase64ToUint8Array(base64) {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(normalized);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function pushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

// Subscribe this device and register it with the Worker for `hostId`.
//
// Returns a status string so the caller can tell the user *why* it failed
// instead of silently doing nothing:
//   'ok'          — subscribed and registered with the Worker
//   'unsupported' — no service worker / PushManager / Notification API
//                   (iOS Safari only exposes these once the app is installed
//                   to the Home Screen, so this is the common "nothing happens")
//   'denied'      — the user blocked notification permission
//   'error'       — subscription or the Worker call failed
export async function enablePush(hostId) {
  if (!pushSupported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  if (Notification.permission !== 'granted') {
    const perm = await Notification.requestPermission();
    if (perm !== 'granted') return 'denied';
  }

  try {
    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });
    }

    const res = await fetch(`${workerBase()}/subscribe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ hostId, subscription: sub.toJSON() }),
    });
    return res.ok ? 'ok' : 'error';
  } catch (e) {
    console.warn('[CAMMIRROR] enablePush failed:', e && e.message);
    return 'error';
  }
}

// Best-effort teardown: tell the Worker to forget us, then drop the local sub.
export async function disablePush() {
  if (!pushSupported()) return;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  try {
    await fetch(`${workerBase()}/unsubscribe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ endpoint: sub.endpoint }),
    });
  } catch (e) {
    /* ignore — we still unsubscribe locally below */
  }
  try {
    await sub.unsubscribe();
  } catch (e) {
    /* already gone */
  }
}

// Host side: ask the Worker to push a cry alert to everyone watching `hostId`.
export async function notifyCry(hostId) {
  if (!hostId) return;
  try {
    await fetch(`${workerBase()}/notify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ hostId }),
    });
  } catch (e) {
    console.warn('[CAMMIRROR] notify failed:', e && e.message);
  }
}

// Parent side: is this monitor currently checking in? The returning-parent
// "resume" shortcut uses this to avoid offering a one-tap rejoin to a monitor
// that's already gone (which would only fail over to the scanner after several
// dead dials). Returns true/false, or null when presence is unknown — the
// backend isn't configured or is unreachable — so the caller can stay optimistic
// and still show the shortcut (e.g. in local dev).
export async function checkHostPresence(hostId) {
  if (!hostId) return null;
  try {
    const res = await fetch(
      `${workerBase()}/presence?hostId=${encodeURIComponent(hostId)}`,
      { method: 'GET' }
    );
    if (!res.ok) return null;
    const data = await res.json();
    return typeof data.live === 'boolean' ? data.live : null;
  } catch (e) {
    return null;
  }
}

// Host side: announce a clean exit as the page goes away, so a normal shutdown
// isn't mistaken for an outage. Uses sendBeacon (built to survive unload) with a
// keepalive-fetch fallback. The body is a JSON string sent as text/plain so the
// beacon needs no CORS preflight; the Worker parses it with request.json()
// regardless of content type.
export function sendGoodbye(hostId) {
  if (!hostId) return;
  const url = `${workerBase()}/goodbye`;
  const body = JSON.stringify({ hostId });
  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'text/plain' });
      if (navigator.sendBeacon(url, blob)) return;
    }
  } catch (e) {
    /* fall through to fetch */
  }
  try {
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch (e) {
    /* best-effort */
  }
}
